import React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { showSnack } from "react-redux-snackbar";
import { IAppState, ErrorDisplay, LoadingDisplay, ApiRequest } from "../../../Common";
import { Application } from "../..";
import { User, UserAssigner } from "../../../User";
import {
  AppAssignmentAction,
  IGetUsersAssignedToAppArguments,
  ISetAppAssignedUsersErroredArguments,
  getUsersAssignedToApp,
  unloadAppAssignedUsers,
  setAppAssignedUsersLoading,
  setAppAssignedUsersErrored
} from "../../Actions/AppAssignmentActions";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../ResponseHandler";
import { ResponseResult } from "../../../Common/Models/ResponseResult";

type Props = {
  application: Application;
};

type StateProps = {
  isLoading: boolean;
  isLoaded: boolean;
  isErrored: boolean;
  errorCode?: number;
  errorDescription?: string;
  assignedUsers: User[];
};

type DispatchProps = {
  getUsersAssignedToApp: (args: IGetUsersAssignedToAppArguments) => void;
  unloadAppAssignedUsers: () => void;
  setAppAssignedUsersLoading: () => void;
  showSnack: (uniqueId: any, args: any) => void;
};

function mapStateToProps(state: IAppState): StateProps {
  return {
    isLoading: state.appAssignment.isLoading,
    isLoaded: state.appAssignment.isLoaded,
    isErrored: state.appAssignment.isErrored,
    errorCode: state.appAssignment.errorCode,
    errorDescription: state.appAssignment.errorMessage,
    assignedUsers: state.appAssignment.users
  };
}

function mapDispatchToProps(
  dispatch: ThunkDispatch<IAppState, {}, AppAssignmentAction>
): DispatchProps {
  return {
    getUsersAssignedToApp: (args: IGetUsersAssignedToAppArguments) =>
      dispatch(getUsersAssignedToApp(args)),
    unloadAppAssignedUsers: () => dispatch(unloadAppAssignedUsers()),
    setAppAssignedUsersLoading: () => dispatch(setAppAssignedUsersLoading()),
    showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
  };
}

class AssignUsersToAppContainer extends React.Component<Props & StateProps & DispatchProps> {
  assignLock: boolean;

  constructor(props: Props & StateProps & DispatchProps) {
    super(props);

    this.onUserAssigned = this.onUserAssigned.bind(this);
    this.onUserRemoved = this.onUserRemoved.bind(this);
    this.releaseAssignLock = this.releaseAssignLock.bind(this);

    this.assignLock = false;
  }

  componentDidMount() {
    this.loadSubscribedUsers();
  }

  componentDidUpdate(prevProps: Props) {
    // make sure the app actually changed
    if (this.props.application.rolePrefix !== prevProps.application.rolePrefix) {
      this.loadSubscribedUsers();
    }
  }

  componentWillUnmount() {
    this.props.unloadAppAssignedUsers();
  }

  onUserAssigned(user: User) {
    if (!this.claimAssignLock()) {
      return;
    }

    // POST assignment
    const body = {
      Id: user.id
    };

    ApiRequest.fromPath(endpoints.applications.assignUser(this.props.application.rolePrefix))
      .withJson(body)
      .withAuthHeader()
      .post()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            this.props.showSnack("ASSIGN_TO_APP", {
              label: `Successfully assigned ${user.name} to ${this.props.application.name}`,
              timeout: 5000
            });

            // reload the list
            this.loadSubscribedUsers();
          },
          (result: ResponseResult) => {
            this.props.showSnack("ASSIGN_TO_APP", {
              label: `Error ${response.status}: ${result.error.message}`,
              timeout: 5000
            });
          },
          () => {
            this.props.showSnack("ASSIGN_TO_APP", {
              label: `Error ${response.status}: An unknown error has occured`,
              timeout: 5000
            });
          }
        );
      })
      .finally(() => {
        this.releaseAssignLock();
      });
  }

  onUserRemoved(user: User) {
    if (!this.claimAssignLock()) {
      return;
    }

    // DELETE assignment
    ApiRequest.fromPath(
      endpoints.applications.unassignUser(this.props.application.rolePrefix, user.id)
    )
      .withAuthHeader()
      .delete()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            this.props.showSnack("UNASSIGN_FROM_APP", {
              label: `Successfully unassigned ${user.name} from ${this.props.application.name}`,
              timeout: 5000
            });

            // reload the list
            this.loadSubscribedUsers();
          },
          (result: ResponseResult) => {
            this.props.showSnack("UNASSIGN_FROM_APP", {
              label: `Error ${response.status}: ${result.error.message}`,
              timeout: 5000
            });
          },
          () => {
            this.props.showSnack("UNASSIGN_FROM_APP", {
              label: `Error ${response.status}: An unknown error has occured`,
              timeout: 5000
            });
          }
        );
      })
      .finally(() => {
        this.releaseAssignLock();
      });
  }

  loadSubscribedUsers() {
    this.props.setAppAssignedUsersLoading();
    this.props.getUsersAssignedToApp({ appId: this.props.application.rolePrefix });
  }

  claimAssignLock() {
    if (!this.assignLock) {
      this.assignLock = true;
      return true;
    }
    return false;
  }

  releaseAssignLock() {
    this.assignLock = false;
  }

  render() {
    if (this.props.isErrored) {
      return (
        <ErrorDisplay
          errorCode={this.props.errorCode}
          errorDescription={this.props.errorDescription}
          errorResult="Could not display App-User Assignments"
        />
      );
    }
    if (this.props.isLoading) {
      return <LoadingDisplay />;
    }
    if (this.props.isLoaded) {
      return (
        <UserAssigner
          assignTo={this.props.application.name}
          assignedUsers={this.props.assignedUsers}
          onUserAssigned={this.onUserAssigned}
          onUserRemoved={this.onUserRemoved}
        />
      );
    }
    return <></>;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AssignUsersToAppContainer);
